<template>
  <div class="reward-key-selector">
    <!-- 输入框和按钮 -->
    <div class="input-group">
      <div class="input-wrapper" @click="openDialog">
        <el-input 
          v-model="localValue" 
          :placeholder="placeholder"
          :disabled="disabled"
          readonly>
        </el-input>
      </div>
      <el-button type="primary" @click="openDialog" :disabled="disabled">
        选择奖励Key
      </el-button>
    </div>

    <!-- 选择弹窗 -->
    <el-dialog
      title="选择奖励Key"
      :visible.sync="dialogVisible"
      width="70%"
      append-to-body
      :modal-append-to-body="false">
      
      <!-- 搜索区域 -->
      <div class="search-area">
        <el-row :gutter="20">
          <el-col :span="8">
            <el-input
              v-model="searchParams.awardsKey"
              placeholder="请输入礼包key"
              clearable>
            </el-input>
          </el-col>
          <el-col :span="6">
            <el-select v-model="searchParams.awardsKeyType" placeholder="请选择类型" clearable>
              <el-option label="普通类" :value="1"></el-option>
              <el-option label="概率类" :value="2"></el-option>
            </el-select>
          </el-col>
          <el-col :span="4">
            <el-select v-model="searchParams.valid" placeholder="状态" clearable>
              <el-option label="有效" :value="1"></el-option>
              <el-option label="无效" :value="0"></el-option>
            </el-select>
          </el-col>
          <el-col :span="6">
            <el-button type="primary" @click="searchRewardKeys">查询</el-button>
            <el-button @click="resetSearch">重置</el-button>
          </el-col>
        </el-row>
      </div>

      <!-- 表格 -->
      <el-table 
        :data="rewardKeyList" 
        border 
        v-loading="loading"
        @row-click="selectRewardKey"
        highlight-current-row
        max-height="400">
        <el-table-column v-if="showMultiSelect" type="selection" width="55"></el-table-column>
        <el-table-column prop="id" label="ID" width="80" align="center"></el-table-column>
        <el-table-column prop="awardsKey" label="礼包Key" width="150" align="center">
          <template slot-scope="scope">
            <span class="key-text" :class="{ 'selected': scope.row.awardsKey === selectedKey }">
              {{ scope.row.awardsKey }}
            </span>
          </template>
        </el-table-column>
        <el-table-column prop="awardsName" label="礼包名称" align="center"></el-table-column>
        <el-table-column prop="awardsKeyType" label="类型" width="100" align="center">
          <template slot-scope="scope">
            {{ scope.row.awardsKeyType === 1 ? '普通类' : '概率类' }}
          </template>
        </el-table-column>
        <el-table-column prop="awardsIcon" label="图标" width="100" align="center">
          <template slot-scope="scope">
            <el-image 
              v-if="scope.row.awardsIcon"
              :src="scope.row.awardsIcon" 
              style="width: 40px; height: 40px;"
              fit="cover">
            </el-image>
          </template>
        </el-table-column>
        <el-table-column prop="valid" label="状态" width="80" align="center">
          <template slot-scope="scope">
            <el-tag v-if="scope.row.valid == 1" type="success" size="mini">有效</el-tag>
            <el-tag v-else type="danger" size="mini">无效</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="100" align="center">
          <template slot-scope="scope">
            <el-button 
              type="primary" 
              size="mini" 
              @click.stop="selectRewardKey(scope.row)">
              选择
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination-area">
        <el-pagination
          background
          layout="prev, pager, next, total"
          :current-page="searchParams.page"
          :page-size="searchParams.pageSize"
          :total="total"
          @current-change="handlePageChange">
        </el-pagination>
      </div>

      <!-- 手动输入区域 -->
      <div class="manual-input-area">
        <el-divider content-position="left">或者手动输入新的Key</el-divider>
        <el-row :gutter="20">
          <el-col :span="18">
            <el-input 
              v-model="manualKey" 
              placeholder="请输入奖励Key（字母、数字、下划线、中划线）"
              @input="validateManualKey">
            </el-input>
            <div v-if="manualKeyError" class="error-text">{{ manualKeyError }}</div>
            <div v-if="isDuplicate" class="error-text">该Key已存在，请选择上面的记录或使用其他Key</div>
          </el-col>
          <el-col :span="6">
            <el-button 
              type="success" 
              @click="useManualKey"
              :disabled="!manualKey || !!manualKeyError || isDuplicate">
              使用此Key
            </el-button>
          </el-col>
        </el-row>
      </div>

      <!-- 底部按钮 -->
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button 
          type="primary" 
          @click="confirmSelection"
          :disabled="!selectedKey">
          确定选择
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'RewardKeySelector',
  props: {
    value: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: '请选择奖励Key'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    showMultiSelect: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      localValue: this.value,
      dialogVisible: false,
      loading: false,
      rewardKeyList: [],
      total: 0,
      selectedKey: '',
      manualKey: '',
      manualKeyError: '',
      isDuplicate: false,
      searchParams: {
        awardsKey: '',
        awardsKeyType: '',
        valid: '',
        page: 1,
        pageSize: 10
      }
    }
  },
  watch: {
    value(newVal) {
      this.localValue = newVal
    },
    localValue(newVal) {
      this.$emit('input', newVal)
    }
  },
  methods: {
    // 打开选择弹窗
    async openDialog() {
      this.dialogVisible = true
      this.selectedKey = this.localValue
      await this.getRewardKeyList()
    },

    // 获取奖励Key列表
    async getRewardKeyList() {
      this.loading = true
      try {
        const { data: res } = await this.$axios({
          method: 'get',
          url: this.$store.getters.path.config_rewardKey_query,
          params: {
            username: this.$store.getters.username,
            token: this.$store.getters.token,
            ...this.searchParams,
          },
        })

        if (res.code === 0 || res.status === 0 || res.code == 200) {
          this.rewardKeyList = res.data.data || []
          this.total = res.data.total || 0
        } else {
          this.$message.error(res.msg || res.message || '获取数据失败')
        }
      } catch (error) {
        console.error('获取奖励Key列表失败:', error)
        this.$message.error('获取数据失败')
      } finally {
        this.loading = false
      }
    },

    // 搜索奖励Key
    searchRewardKeys() {
      this.searchParams.page = 1
      this.getRewardKeyList()
    },

    // 重置搜索
    resetSearch() {
      this.searchParams = {
        awardsKey: '',
        awardsKeyType: '',
        valid: '',
        page: 1,
        pageSize: 10
      }
      this.getRewardKeyList()
    },

    // 分页处理
    handlePageChange(page) {
      this.searchParams.page = page
      this.getRewardKeyList()
    },

    // 选择奖励Key
    selectRewardKey(row) {
      this.selectedKey = row.awardsKey
      this.manualKey = ''
      this.manualKeyError = ''
      this.isDuplicate = false
    },

    // 验证手动输入的Key
    validateManualKey() {
      this.manualKeyError = ''
      this.isDuplicate = false

      if (!this.manualKey) {
        return
      }

      // 验证格式
      const keyPattern = /^[a-zA-Z0-9_-]+$/
      if (!keyPattern.test(this.manualKey)) {
        this.manualKeyError = '只能输入字母、数字、下划线和中划线'
        return
      }

      // 检查是否重复
      const existingKey = this.rewardKeyList.find(item => item.awardsKey === this.manualKey)
      if (existingKey) {
        this.isDuplicate = true
      }
    },

    // 使用手动输入的Key
    useManualKey() {
      if (!this.manualKey || this.manualKeyError || this.isDuplicate) {
        return
      }
      this.selectedKey = this.manualKey
    },

    // 确认选择
    confirmSelection() {
      if (!this.selectedKey) {
        this.$message.warning('请选择一个奖励Key')
        return
      }
      
      this.localValue = this.selectedKey
      this.dialogVisible = false
      this.$emit('change', this.selectedKey)
    }
  }
}
</script>

<style scoped lang="scss">
.reward-key-selector {
  .input-group {
    display: flex;
    align-items: center;

    .input-wrapper {
      flex: 1;
      cursor: pointer;
    }
    
    .input-wrapper .el-input__inner {
      cursor: pointer !important;
    }

    .el-button {
      margin-left: 10px;
    }
  }
}

.search-area {
  margin-bottom: 20px;
  padding: 16px;
  background-color: #f5f7fa;
  border-radius: 4px;
}

.pagination-area {
  margin: 20px 0;
  text-align: center;
}

.manual-input-area {
  margin-top: 20px;
  
  .error-text {
    color: #f56c6c;
    font-size: 12px;
    margin-top: 4px;
  }
}

.key-text {
  cursor: pointer;
  
  &.selected {
    color: #409eff;
    font-weight: bold;
  }
}

::v-deep .el-table__row:hover {
  background-color: #f5f7fa;
  cursor: pointer;
}

::v-deep .el-table__row.current-row {
  background-color: #ecf5ff;
}

::v-deep .el-dialog__wrapper {
  z-index: 3100 !important;
}

::v-deep .el-overlay {
  z-index: 3099 !important;
}
</style> 